import { useState } from 'react';
import { ShieldAlert, X } from 'lucide-react';
import { useFeatures } from '../contexts/FeatureContext';

/**
 * Subtle pill that appears in the bottom-right corner when the current
 * session is running in "simulated production" mode (i.e. the `?features=prod`
 * override is active but we're not actually on a production backend).
 *
 * Clicking the pill clears the override and reloads so the full feature set
 * returns. This is intentionally only rendered in dev/staging previews — it
 * hides itself in real production and when no override is present.
 */
export default function ProdModeIndicator() {
  const { isProdOverride, clearOverride, mode } = useFeatures();
  const [dismissed, setDismissed] = useState(false);

  if (!isProdOverride || dismissed) return null;

  const handleExit = () => {
    clearOverride();
    // Full reload so the backend feature fetch re-runs and any stale
    // component state (e.g. sidebar filtering) refreshes cleanly.
    window.location.reload();
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] flex items-center gap-2 rounded-full border border-amber-500/40 bg-slate-900/90 px-3 py-1.5 text-xs text-amber-200 shadow-lg backdrop-blur"
      data-testid="prod-mode-pill"
    >
      <ShieldAlert className="h-3.5 w-3.5" />
      <span className="font-medium uppercase tracking-wide">Prod Preview</span>
      <span className="text-slate-400">·</span>
      <button
        type="button"
        onClick={handleExit}
        className="text-amber-200 hover:text-white transition-colors"
        data-testid="prod-mode-exit-btn"
        title={`Exit production preview (current backend mode: ${mode})`}
      >
        Exit
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="ml-1 text-slate-500 hover:text-slate-200 transition-colors"
        data-testid="prod-mode-dismiss-btn"
        title="Hide for this session"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
